import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge } from '@/components/ui';
import { useMaintenanceRoutes } from './hooks/useRoutes';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { RouteTable } from './components/RouteTable';
import { RouteFormModal } from './components/RouteFormModal';
import type { MaintenanceRoute, RouteStatus } from './types/routes.types';

const statusLabels: Record<RouteStatus, string> = {
  draft: 'Bản nháp',
  active: 'Đang hoạt động',
  inactive: 'Ngừng hoạt động',
  assigned: 'Đã giao tuyến',
  in_progress: 'Đang thực hiện',
  completed: 'Đã hoàn thành',
  cancelled: 'Đã hủy',
  archived: 'Lưu trữ',
};

export default function RouteListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();
  
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<MaintenanceRoute | null>(null);

  const {
    routes,
    total,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useMaintenanceRoutes(companyId);

  return (
    <PageLayout
      title="Tuyến bảo dưỡng"
      description="Quản lý các tuyến di chuyển thực địa, điểm dừng (stops) và danh mục công việc kiểm tra định kỳ."
      breadcrumbs={[{ label: 'Bảo dưỡng' }, { label: 'Tuyến bảo dưỡng' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-download-line">Xuất danh sách</Button>
          <AuthButton 
            module="maintenance" 
            action="create" 
            variant="primary" 
            icon="ri-add-line" 
            onClick={() => setShowCreate(true)}
          >
            Tạo tuyến mới
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng tuyến"
            value={stats?.totalRoutes || 0}
            icon="ri-road-map-line"
            variant="neutral"
          />
          <KPICard
            title="Đang hoạt động"
            value={stats?.activeRoutes || 0}
            icon="ri-play-circle-line"
            variant="success"
          />
          <KPICard
            title="Lịch trình hôm nay"
            value={stats?.pendingAssignments || 0}
            icon="ri-calendar-todo-line"
            variant="warning"
          />
          <KPICard
            title="Hoàn thành (24h)"
            value={stats?.completedToday || 0}
            icon="ri-checkbox-circle-line"
            variant="info"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm tên tuyến, mã, mô tả..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <RouteTable 
            data={routes} 
            isLoading={isLoading} 
            onEdit={(item) => setEditItem(item)}
            onRowClick={(item) => navigate(`./detail/${item.id}`)} 
          />
        </Card>
      </div>

      <RouteFormModal
        isOpen={showCreate || !!editItem}
        item={editItem}
        companyId={companyId}
        onClose={() => {
          setShowCreate(false);
          setEditItem(null);
        }}
      />
    </PageLayout>
  );
}
